import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { type Carousel } from 'react-responsive-carousel'
import { useNavigate } from 'react-router-dom'
import { createGlobalStyle } from 'styled-components'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/pagination'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import {
    ImageFile,
    PaymentModal,
    VideoFile
} from 'components-ref/molecules'
import { Modal } from 'components-ref/molecules/modal/modal'
import { usePreviewContext } from 'context/previewContext'
import { useUserProfile } from 'store'
import colors from 'styles/colors'
import {
    type IPost,
    type IPostMediaPreview
} from 'types/interfaces/ITypes'

interface Props {
    postIndex: number
    postData: IPost
    dataQuery: readonly any[]
    purchaseCb: (storeCvc?: boolean, cvc?: string) => void
    processing: boolean
    paymentMethod: {
        method: 'card' | 'wallet'
        cardId: string | undefined
    }
    setPaymentMethod: (method: {
        method: 'card' | 'wallet'
        cardId: string | undefined
    }) => void
}

export const PremiumPost = ({
    postIndex,
    postData,
    dataQuery,
    purchaseCb,
    processing,
    paymentMethod,
    setPaymentMethod
}: Props) => {
    const previewFiles = useMemo<IPostMediaPreview[]>(() => {
    return [
        ...(postData.photos_preview ?? []),
        ...(postData.videos_preview ?? [])
    ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    }, [postData.photos_preview, postData.videos_preview])

    const hasNoPreview =
    previewFiles.length > 0 &&
    previewFiles.every(file => !file.preview?.src)
    const [confirmBuyModalOpen, setConfirmBuyModalOpen] = useState(false)

    const previewData = usePreviewContext()
    const carouselRef = useRef<Carousel>(null)
    const sliderWrapper = useRef<HTMLDivElement>(null)
    const swiperRef = useRef(null)

    const { t } = useTranslation()
    const navigate = useNavigate()
    const userData = useUserProfile()

    useEffect(() => {
        if (!carouselRef.current || !sliderWrapper.current) {
            return
        }

        const postsCarousel = sliderWrapper.current.getElementsByClassName(
            'post__files__carousel'
        )?.[0]

        if (!postsCarousel) {
            return
        }

        const sliderRef = postsCarousel.getElementsByClassName(
            'slider'
        )?.[0] as HTMLUListElement

        // Only apply if multiple slides
        if (sliderRef && previewFiles.length > 1) {
            sliderRef.style.transition = 'none'
            sliderRef.style.transform = 'translate3d(-100%, 0px, 0px)'

            setTimeout(() => {
                sliderRef.style.transition = ''
            }, 0)
        }
    }, [
        carouselRef.current,
        sliderWrapper.current,
        previewFiles.length
    ])

    const formattedPrice =
        postData?.price !== null && postData?.price !== undefined
            ? postData.price % 1 === 0
                ? postData.price.toFixed(0)
                : postData.price.toFixed(2)
            : '0.00'

    const noDefaultCard =
        userData?.default_card == null ||
        String(userData?.default_card) === ''

    const noWalletDeposit =
        userData?.wallet_deposit == null ||
        Number(userData.wallet_deposit) <= 0

    const showAddCard =
        noDefaultCard &&
        noWalletDeposit

    const handleBuy = () => {
        if (
            userData?.status === 'restricted' &&
            userData?.role === 'fan'
        ) {
            addToast(
                'error',
                'Your account is restricted.'
            )

            return
        }

        setConfirmBuyModalOpen(true)
    }

    if (previewFiles.length === 0 && postData.body) {
        return (
            <div className='post-tags-wrapper post-tags-wrapper--premium'>
                <div className='post__files__file post__files__file--no-media'>
                    {postData.body}
                </div>
            </div>
        )
    }

    return (
        <div
            className={`post-tags-wrapper post-tags-wrapper--premium ${
            hasNoPreview ? 'post-tags-wrapper--no-preview' : ''
            }`}
            ref={sliderWrapper}
            style={{
                position: 'relative'
            }}
        >
            <div className='post__files__carousel'>
                {previewFiles.length === 1 ? (
                    (() => {
                        const file = previewFiles[0]

                        if (
                            file.preview &&
                            (
                                file.preview.type === 'thumb' ||
                                file.preview.type === 'blur'
                            )
                        ) {
                            return (
                                <ImageFile
                                    key={file.id}
                                    fileIndex={0}
                                    file={file}
                                    postIndex={postIndex}
                                    postData={postData}
                                    dataQuery={dataQuery}
                                    previewData={previewData}
                                    purchaseCb={purchaseCb}
                                    paymentMethod={paymentMethod}
                                    setPaymentMethod={setPaymentMethod}
                                    processing={processing}
                                />
                            )
                        } else if (
                            file.preview &&
                            file.preview.type === 'clip'
                        ) {
                            return (
                                <VideoFile
                                    key={file.id}
                                    fileIndex={0}
                                    file={file}
                                    postIndex={postIndex}
                                    postData={postData}
                                    dataQuery={dataQuery}
                                    previewData={previewData}
                                    purchaseCb={purchaseCb}
                                    processing={processing}
                                    paymentMethod={paymentMethod}
                                    setPaymentMethod={setPaymentMethod}
                                />
                            )
                        }

                        return null
                    })()
                ) : (
                    <>
                        <div className='post-tags-wrapper'>
                            <PaginationBullet />

                            <Swiper
                                direction='horizontal'
                                spaceBetween={10}
                                slidesPerView={1}
                                slidesPerGroup={1}
                                ref={swiperRef}
                                className='post__files__carousel'
                                longSwipesRatio={0.1}
                                resistanceRatio={0}
                                pagination={{
                                    clickable: true
                                }}
                                mousewheel={true}
                                modules={[Pagination]}
                            >
                                {previewFiles
                                    .filter(
                                        file =>
                                            file.preview.type !== 'blur'
                                    )
                                    .flatMap(
                                        (
                                            file: IPostMediaPreview,
                                            index: number
                                        ) => {
                                            if (
                                                (
                                                    file.preview &&
                                                    (
                                                        file.preview.type === 'thumb' ||
                                                        file.preview.type === 'blur'
                                                    )
                                                ) ||
                                                file.preview.type === 'placeholder'
                                            ) {
                                                return (
                                                    <SwiperSlide
                                                        key={file.id}
                                                    >
                                                        <ImageFile
                                                            key={file.id}
                                                            file={file}
                                                            fileIndex={index}
                                                            postIndex={postIndex}
                                                            postData={postData}
                                                            dataQuery={dataQuery}
                                                            previewData={
                                                                previewData
                                                            }
                                                            purchaseCb={
                                                                purchaseCb
                                                            }
                                                            paymentMethod={
                                                                paymentMethod
                                                            }
                                                            setPaymentMethod={
                                                                setPaymentMethod
                                                            }
                                                            processing={
                                                                processing
                                                            }
                                                        />
                                                    </SwiperSlide>
                                                )
                                            } else if (
                                                file.preview &&
                                                file.preview.type === 'clip'
                                            ) {
                                                return (
                                                    <SwiperSlide
                                                        key={file.id}
                                                    >
                                                        <VideoFile
                                                            key={file.id}
                                                            file={file}
                                                            fileIndex={index}
                                                            postIndex={postIndex}
                                                            postData={postData}
                                                            dataQuery={dataQuery}
                                                            previewData={
                                                                previewData
                                                            }
                                                            purchaseCb={
                                                                purchaseCb
                                                            }
                                                            processing={
                                                                processing
                                                            }
                                                            paymentMethod={
                                                                paymentMethod
                                                            }
                                                            setPaymentMethod={
                                                                setPaymentMethod
                                                            }
                                                        />
                                                    </SwiperSlide>
                                                )
                                            }

                                            return []
                                        }
                                    )}
                            </Swiper>
                        </div>
                    </>
                )}
            </div>

            <div
                style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: '2.5rem',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    pointerEvents: 'none'
                }}
            >
                <div
                    style={{
                        pointerEvents: 'auto'
                    }}
                >
                    <Button
                        text={
                            <p>
                                {t('unlock')}{' '}
                                <span className='post__cta__button__price'>
                                    ${formattedPrice}
                                </span>
                            </p>
                        }
                        color='black'
                        font='mont-14-normal'
                        height='3'
                        width='fit'
                        padding='2'
                        customClass='post__cta__button'
                        clickFn={handleBuy}
                        disabled={processing}
                    />
                </div>
            </div>

            {confirmBuyModalOpen && (
                <Modal
                    onClose={() => {
                        setConfirmBuyModalOpen(false)
                    }}
                    title={
                        showAddCard
                            ? 'Add A Payment Card'
                            : 'Select Payment Method'
                    }
                    closeOnLayerClick={true}
                    isLayerTransparent={false}
                >
                    {showAddCard ? (
                        <ConfirmModal
                            confirmText='Manage Cards'
                            customClass='confirmModal--add-card'
                            onCancel={() => {
                                setConfirmBuyModalOpen(false)
                            }}
                            body={
                                <div>
                                    <p className='add-card-title-spacing'>
                                        You don&apos;t have a saved card
                                        or wallet funds. Please add a card
                                        to continue.
                                    </p>
                                </div>
                            }
                            confirmFn={() => {
                                navigate(
                                    '/settings/general/wallet'
                                )

                                setConfirmBuyModalOpen(false)
                            }}
                        />
                    ) : (
                        <PaymentModal
                            purchaseCb={purchaseCb}
                            paymentMethod={
                                paymentMethod as {
                                    method: 'card' | 'deposit'
                                    cardId: string | undefined
                                }
                            }
                            setPaymentMethod={
                                setPaymentMethod as (method: {
                                    method: 'card' | 'deposit'
                                    cardId: string | undefined
                                }) => void
                            }
                            price={postData?.price as number}
                        />
                    )}
                </Modal>
            )}
        </div>
    )
}

const PaginationBullet = createGlobalStyle`
    .swiper-pagination > .swiper-pagination-bullet-active {
        background-color: ${colors.white};
    }
`